import { Box, Button, CircularProgress, Grid, MenuItem, Select, Slider, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../hooks/useApi";
import { useLanguage } from "../../hooks/useLanguage";
import { debounce } from "../../utils/lib";

/**
 * Enables configuration and import of the specified product.
 * @param {*} param0 
 * @returns 
 */
export default function ProductConfiguration({ product }) {
  // common data
  const [data, setData] = useState({});
  // the data-cells
  const [cells, setCells] = useState({});
  // state for the loading-overlay
  const [isLoading, setIsLoading] = useState(false);

  const { locale } = useLanguage();

  const navigate = useNavigate();

  const api = useApi();

  /**
  * Get the current language from the locale.
  * @returns 
  */
  const getLanguage = () => {
    switch (locale) {
      case "de":
        return "german";
      default:
        return "english";
    }
  };

  /**
   * Selects the line specified by the props.
   */
  useEffect(() => {
    if (!product || !product.tableId || !product.lineId) return;
    setIsLoading(true);
    api
      .get(`/3dfindit/details/select?language=${getLanguage()}&tableId=${encodeURIComponent(product.tableId)}&lineId=${product.lineId}`)
      .then(response => {
        setData(response.data);
        // only one line included in response
        setCells(response.data.lines[0]);
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      })
  }, [product]);

  /**
   * Change the specified variable to the specified value of the current line.
   * @param {*} variableName 
   * @param {*} variableValue 
   */
  const changeSelectedLine = (variableName, variableValue) => {
    api
      .get(`/3dfindit/details/change?language=${getLanguage()}&tableId=${encodeURIComponent(data.tableId)}
      &mident=${encodeURIComponent(data.mident)}&variableName=${variableName}&variableValue=${variableValue}`)
      .then(response => {
        // data is updated
        setData(response.data);
        setCells(response.data.lines[0]);
      })
      .catch(error => {
        console.log(error);
      });
  };

  /**
   * Element that displays a modifiable numeric range.
   * @param {*} param0 
   * @returns 
   */
  const RangeElement = ({ defaultValue, lowerBound, upperBound, useStep, step, index }) => {
    const [value, setValue] = useState(Number(defaultValue));

    return <Slider
      value={value}
      min={Number(lowerBound)}
      max={Number(upperBound)}
      step={useStep ? Number(step) : 1}
      valueLabelDisplay="auto"
      onChange={(_, newValue) => setValue(newValue)}
      onChangeCommitted={(_, newValue) => {
        setValue(newValue);
        changeSelectedLine(product.columns[index].name, newValue);
      }}
    />
  };

  /**
   * Element that displays a selectable dropdown of named values.
   * @param {*} param0 
   * @returns 
   */
  const SelectionElement = ({ defaultValue, options, index }) => {
    const [value, setValue] = useState(defaultValue);

    return <Select
      value={value}
      onChange={(event) => {
        setValue(event.target.value);
        changeSelectedLine(product.columns[index].name, event.target.value);
      }}
    >
      {options.map((option, index) => <MenuItem value={option.name} key={index} >{option.value}</MenuItem>)}
    </Select>
  };

  // debounces the changeSelectedLine-call of the TextFieldElement
  const handleTextFieldSelect = debounce(changeSelectedLine, 500);

  /**
   * Element that displays a text-field.
   * @param {*} param0 
   * @returns 
   */
  const TextFieldElement = ({ defaultValue, index }) => {
    const [value, setValue] = useState(defaultValue);

    return <TextField
      value={value}
      onChange={(event) => {
        setValue(event.target.value);
        handleTextFieldSelect(product.columns[index].name, event.target.value);
      }}
    />
  }

  /**
   * All rows of the selected product-line.
   */
  const rows = useMemo(() => {
    return product.columns.map((column, index) => {
      if (!cells.cellValues) return;
      let cell = cells.cellValues[index];
      let valueCell;

      if (column.variableType === "MM_FW") {
        valueCell = null;
        if (column.valueRangeType === "FWTYP_NAMED") {
          if (cell.locked) {
            valueCell = <p style={{ wordBreak: "break-word" }} >{cell.value}</p>
          } else {
            // enable selection
            valueCell = <SelectionElement
              defaultValue={column.dataType === "Z" ? Number(cell.internalValue).toFixed(0) : cell.internalValue}
              options={cell.values}
              index={index}
            />
          }
        } else {
          // is range
          if (cell.locked) {
            valueCell = <p style={{ wordBreak: "break-word" }} >{cell.value}</p>
          } else {
            // enable selection
            const cellsNumeric = cell.ranges.filter(r => r.isNumeric);
            const cellsText = cell.ranges.filter(r => !r.isNumeric);
            const typesNumeric = cellsNumeric.map(r => r.vraType);
            const typesText = cellsText.map(r => r.vraType);

            if (typesNumeric.indexOf('VRATYPE_RANGE') !== -1) {
              // display slider selection
              const range = cellsNumeric[typesNumeric.indexOf('VRATYPE_RANGE')];
              valueCell = <RangeElement defaultValue={cell.value} lowerBound={range.lowerBound} upperBound={range.upperBound}
                useStep={range.useStep} step={range.step} index={index} />;
            } else if (typesNumeric.indexOf('VRATYPE_DISK') !== -1) {
              // display named selection
              valueCell = <SelectionElement defaultValue={column.dataType === "Z" ? Number(cell.internalValue).toFixed(0) : cell.internalValue}
                options={cellsNumeric.map(r => ({ name: r.name, value: r.value }))} index={index} />;
            } else if (typesText.indexOf('VRATYPE_DISK') !== -1 && typesText.indexOf('VRATYPE_RANGE') === -1) {
              // display named selection
              valueCell = <SelectionElement defaultValue={column.dataType === "Z" ? Number(cell.internalValue).toFixed(0) : cell.internalValue}
                options={cellsText.map(r => ({ name: r.name, value: r.value }))} index={index} />;
            } else {
              // display text-field
              valueCell = <TextFieldElement defaultValue={cell.internalValue} index={index} />
            }
          }
        }
      } else {
        // not a range-variable
        valueCell = <p style={{ wordBreak: "break-word" }} >{cell.value}</p>;
      }

      return <Grid key={index} item xs={12} spacing={2} container direction="row" >
        <Grid item xs={12} ><div style={{ backgroundColor: 'grey', width: '100%', height: '1px' }} ></div></Grid>
        <Grid item xs={12} sm={6} container sx={{ justifyContent: 'center', alignItems: 'center' }} >
          {column.description ? column.description : column.name}<p style={{ color: 'grey', display: 'inline' }} >&nbsp;{column.unit ? "(" + column.unit + ")" : ""}</p>
        </Grid>
        <Grid item xs={12} sm={6} container sx={{ justifyContent: 'center', alignItems: 'center' }} >{valueCell}</Grid>
      </Grid>
    });
  }, [cells]);

  /**
     * Creates the specified product and then navigates to it.
     */
  const createProduct = () => {
    if (!data.mident) return;
    setIsLoading(true);
    api
      .get(`/3dfindit/product?language=german&mident=${encodeURIComponent(data.mident)}`)
      .then(response => {
        navigate(`/product/${response.data.id}`);
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div style={{ height: '100%' }} >
      {isLoading && <Box
        sx={{
          marginTop: 3,
          marginBottom: 3,
          position: "fixed",
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10
        }}
      >
        <CircularProgress color="primary" />
      </Box>}
      <h1>{data.standardNumber}</h1>
      <h2>{data.standardName}</h2>
      <Grid container spacing={2} direction="column" sx={{ alignItems: "center" }} >
        {rows}
      </Grid>
      <div style={{ width: '100%', height: '60px', position: 'sticky', bottom: '-20px', backgroundColor: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'center' }} >
        <Button variant="contained" onClick={createProduct} ><FormattedMessage id="ImportFindit3d.importProduct" /></Button>
      </div>
    </div>
  );
};